// Veercal meter rules, v2 ("Tell-tale"), for the React app.
// Static-page equivalent: public/design/v2/meter-rules.js.
//
// These are the ONLY meters Veercal shows, approved by Pat on 16 Oct 2026
// (docs/brand/DESIGN.md §5; reasoning and sources in the "Veercal meter rules"
// doc). <Meter value={r.lit} label={r.aria} /> only draws the lit count this
// returns; the rating happens here and nowhere else. Never add a meter or move
// a band edge without Pat's written approval.
//
// The block between BEGIN and END SHARED RULES must stay identical (apart from
// indentation) to the same block in public/design/v2/meter-rules.js;
// meterRules-sync.test.js fails if they differ.
//
//   const r = rateCheck({ rate: 12.1, benchmark: 10.4, period: 'March to August 2026' });
//   // r.lit === 3, r.label === 'Above average', r.tap === the full tap text
//   const e = equityAtExit({ owing: 29431, value: 35264, when: 'at the end of the lease' });
// Both return null when an input is missing, and then no meter shows.

/* eslint-disable no-var */
// BEGIN SHARED RULES
var RULES = {
  equity: {
    name: 'Equity at exit',
    // x = amount owing at exit / estimated value at exit, as a whole percentage.
    // 88 matches end-of-term.html's TRADE_IN_HAIRCUT (a dealer trade-in at 88% of value).
    edges: [70, 88, 100, 115],
    labels: ['Well covered', 'Covered', 'Tight', 'Shortfall', 'Large shortfall'],
    rule: 'Rule: 1 lit up to 70%, 2 up to 88% (a dealer trade-in still covers it), 3 up to 100%, 4 up to 115%, 5 above that.'
  },
  rate: {
    name: 'Rate check',
    // g = interest rate - benchmark, in percentage points to one decimal place.
    // Benchmark: RBA table F8, series FLRPFNFTF (new fixed-rate personal fixed-term
    // loans), average of the latest 6 months, refreshed quarterly.
    edges: [-1.0, 1.0, 4.0, 8.0],
    labels: ['Below average', 'About average', 'Above average', 'Well above average', 'Far above average'],
    sentences: [
      'Below the average.',
      'Close to the average.',
      'Above the average. Some mainstream car lenders\' typical rates sat here in ASIC\'s 2026 review.',
      'Higher than any mainstream car lender\'s typical rate in ASIC\'s 2026 review.',
      'In the range of the highest-cost lender in ASIC\'s 2026 review.'
    ],
    rule: 'Rule: 1 lit at 1 point or more below average, 2 within 1 point, 3 up to 4 points above, 4 up to 8 above, 5 more than 8 above.'
  }
};

// Upper edges are inclusive: a value equal to an edge lights the lower count.
function litFor(value, edges) {
  for (var i = 0; i < edges.length; i++) {
    if (value <= edges[i]) return i + 1;
  }
  return edges.length + 1;
}

function isNum(v) { return typeof v === 'number' && isFinite(v); }
function money(v) { return '$' + Math.round(v).toLocaleString('en-AU'); }
function ratePct(v) { return String(Number(v.toFixed(2))); }

// Equity at exit: { owing, value, when?, source?, fmt? }
//   owing  amount still owing at the exit point (payout, residual, balloon or loan balance)
//   value  estimated value of the car at that point
//   when   e.g. 'at the end of the lease' (default: 'when you plan to get out')
//   source where the value came from, e.g. 'your own estimate'
function equityAtExit(input) {
  var owing = input ? input.owing : undefined;
  var value = input ? input.value : undefined;
  if (!isNum(owing) || !isNum(value) || owing <= 0 || value <= 0) return null;
  var fmt = input.fmt || money;
  var x = Math.round((owing / value) * 100);
  var lit = litFor(x, RULES.equity.edges);
  var label = RULES.equity.labels[lit - 1];
  var sentence = lit <= 2 ? 'The estimated value covers what you\'d owe.'
    : lit === 3 ? 'The estimated value covers it, but a dealer trade-in at 88% of value might not.'
      : 'You\'d owe about ' + fmt(owing - value) + ' more than the estimated value.';
  var when = input.when || 'when you plan to get out';
  var source = input.source ? ' The value is ' + input.source + '.' : '';
  return {
    id: 'equity',
    name: RULES.equity.name,
    lit: lit,
    label: label,
    value: x,
    sentence: sentence,
    aria: RULES.equity.name + ': ' + lit + ' of 5, ' + label,
    tap: 'You\'d owe ' + fmt(owing) + ' ' + when + '. The car is estimated to be worth ' + fmt(value) +
      ' then, so you\'d owe ' + x + '% of its value. ' + sentence + source + ' ' + RULES.equity.rule
  };
}

// Rate check: { rate, benchmark, period? }, both in per cent a year (12.1 means 12.1%).
// rate is the contract interest rate, never the comparison rate.
function rateCheck(input) {
  var rate = input ? input.rate : undefined;
  var benchmark = input ? input.benchmark : undefined;
  if (!isNum(rate) || !isNum(benchmark) || rate < 0 || benchmark <= 0) return null;
  // Work in hundredths to avoid floating-point drift, then round to one decimal place.
  var g = Math.round((Math.round(rate * 100) - Math.round(benchmark * 100)) / 10) / 10;
  if (g === 0) g = 0; // no "-0"
  var lit = litFor(g, RULES.rate.edges);
  var label = RULES.rate.labels[lit - 1];
  var sentence = RULES.rate.sentences[lit - 1];
  var period = input.period ? ' (' + input.period + ')' : '';
  var gap = g === 0 ? 'so yours is the same.'
    : 'so yours is ' + Math.abs(g).toFixed(1) + ' points ' + (g > 0 ? 'above' : 'below') + ' it.';
  return {
    id: 'rate',
    name: RULES.rate.name,
    lit: lit,
    label: label,
    value: g,
    sentence: sentence,
    aria: RULES.rate.name + ': ' + lit + ' of 5, ' + label,
    tap: 'Your rate is ' + ratePct(rate) + '%. The RBA\'s average for new fixed-rate personal loans was ' +
      benchmark.toFixed(1) + '%' + period + ', ' + gap + ' ' + sentence +
      ' Rates depend on the loan and your credit history. ' + RULES.rate.rule
  };
}
// END SHARED RULES

export { RULES, litFor, equityAtExit, rateCheck };
